import { Download, Edit } from 'lucide-react'
import { useState, useEffect, useRef, useContext } from 'react'
import { toast } from 'sonner'

import axiosInstance from '@/api/axiosInstance'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { businessTypeOptions, countryCodeOptions } from '@/config'
import { AuthContext } from '@/contexts/auth-context'
import { RiContrastDropLine } from 'react-icons/ri'
import logger from '../../../utils/logger'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif']

function BusinessProfileComponent() {
  const { checkAuthUser } = useContext(AuthContext)
  const [profileData, setProfileData] = useState({
    business_name: '',
    email: '',
    mobile: '',
    country_code: 'AE +971',
    address: '',
    business_type: '',
    bankAccountNumber: '',
    bankName: '',
    ifscCode: '',
    profile_image: '',
  })
  const [loading, setLoading] = useState(true)
  const [loading1, setLoading1] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [imageError, setImageError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [bankError, setBankError] = useState({
    bankName: '',
    bankAccountNumber: '',
    ifscCode: '',
  })

  const fileInputRef = useRef(null)

  const validateImage = (file) => {
    if (!file) {
      setImageError('')
      return true
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setImageError('Only JPG, JPEG, PNG, GIF images are allowed')
      return false
    }

    if (file.size > MAX_FILE_SIZE) {
      setImageError('Image size must be less than 5MB')
      return false
    }

    setImageError('')
    return true
  }

  const validatePhoneNumber = (countryCode, phoneNumber) => {
    const country = countryCodeOptions.find((opt) => opt.id === countryCode)
    if (!country) {
      setPhoneError('Invalid country code')
      return false
    }

    if (!phoneNumber) {
      setPhoneError('Phone number is required')
      return false
    }

    if (phoneNumber.length !== country.phoneLength) {
      setPhoneError(`Phone number must be ${country.phoneLength} digits`)
      return false
    }

    setPhoneError('')
    return true
  }

  // validate bank details
  const validateBankDetails = () => {
    let valid = true
    const errors = { bankName: '', bankAccountNumber: '', ifscCode: '' }

    // Bank name
    if (!profileData.bankName.trim()) {
      errors.bankName = 'Bank name is required'
      valid = false
    } else if (!/^[A-Za-z ]+$/.test(profileData?.bankName?.trim())) {
      errors.bankName = 'Bank name must contain only letters'
      valid = false
    }

    // Account number
    if (!profileData?.bankAccountNumber?.toString()?.trim()) {
      errors.bankAccountNumber = 'Account number is required'
      valid = false
    } else if (!/^[0-9]{8,18}$/.test(profileData?.bankAccountNumber?.toString()?.trim())) {
      errors.bankAccountNumber = 'Account number must be 8–18 digits'
      valid = false
    }

    // IFSC code
    if (!profileData.ifscCode.trim()) {
      errors.ifscCode = 'IFSC code is required'
      valid = false
    } else if (!/^[A-Z]{4}0[0-9]{6}$/.test(profileData?.ifscCode?.trim().toUpperCase())) {
      errors.ifscCode = 'Invalid IFSC code format'
      valid = false
    }

    setBankError(errors)
    return valid
  }

  const getProfile = async () => {
    try {
      const response = await axiosInstance.get('web-api/get-profile-details')
      if (response.data.status === 200) {
        const data = response.data.data
        setProfileData({
          business_name: data.business_name || '',
          email: data.email || '',
          mobile: data.mobile || '',
          country_code: data.country_code || 'AE +971',
          address: data.address || '',
          business_type: data.business_type || '',
          bankAccountNumber: data.bankAccountNumber || '',
          bankName: data.bankName || '',
          ifscCode: data.ifscCode || '',
          profile_image: data.profile_image || '',
        })
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
      toast.error('Failed to fetch profile data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getProfile()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Validate phone number when it changes
    if (name === 'mobile') {
      validatePhoneNumber(profileData.country_code, value)
    }
  }

  const handleCountryCodeChange = (value) => {
    setProfileData((prev) => ({
      ...prev,
      country_code: value,
      mobile: '',
    }))
  }

  const handleBusinessTypeChange = (value) => {
    setProfileData((prev) => ({
      ...prev,
      business_type: value,
    }))
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file) {
      setSelectedImage(null)
      return
    }

    if (!validateImage(file)) {
      e.target.value = ''
      return
    }

    setSelectedImage(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setProfileData((prev) => ({
        ...prev,
        profile_image: reader.result,
      }))
    }
    reader.readAsDataURL(file)
  }

  const handleSave = async () => {
    // Validate business name
    if (!profileData.business_name.trim()) {
      toast.error('Business name cannot be empty')
      return
    }

    // Validate phone number
    if (!validatePhoneNumber(profileData.country_code, profileData.mobile)) {
      return
    }

    // Validate bank details
    if (!validateBankDetails()) {
      return
    }

    try {
      setLoading1(true)
      const formData = new FormData()

      logger.log('formData =====> ', formData)

      Object.keys(profileData).forEach((key) => {
        if (key !== 'profile_image') {
          formData.append(key, profileData[key])
        }
      })

      if (selectedImage) {
        formData.append('profile_image', selectedImage)
      }

      const response = await axiosInstance.put('web-api/update-profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.status === 200) {
        toast.success('Profile updated successfully')
        setIsEditing(false)
        setSelectedImage(null)
        getProfile()
        checkAuthUser()
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error(error.response?.data?.message || 'Failed to update profile')
    } finally {
      setLoading1(false)
    }
  }

  const getCurrentCountry = () => {
    return (
      countryCodeOptions.find((opt) => opt.id === profileData.country_code) ||
      countryCodeOptions.find((opt) => opt.id === 'AE +971')
    )
  }

  if (loading) {
    return <div className="flex justify-center py-8">Loading...</div>
  }

  return (
    <div
      className="max-w-4xl mx-auto p-4 md:p-6 "
      style={{
        background: '#f7f8fc20',
      }}
    >
      <h1 className="text-2xl font-bold mb-6 w-full text-center">Business Profile</h1>
      <div className="space-y-6">
        <div>
          <div className="flex flex-col items-center">
            <div className="relative group">
              <img
                src={
                  selectedImage
                    ? profileData.profile_image
                    : profileData.profile_image
                      ? `${import.meta.env.VITE_BASE_URL}${profileData.profile_image}`
                      : '/profile.svg'
                }
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
              />
              {isEditing && (
                <Label
                  htmlFor="profileImage"
                  className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <Edit size={25} className=" text-purple-600 shadow-2xl " />

                  <Input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                  />
                </Label>
              )}
            </div>
            {imageError && <p className="mt-2 text-sm text-destructive">{imageError}</p>}

            <p className="text-sm text-muted-foreground text-center mt-1">
              {isEditing ? (
                <>( Max size: 5MB. Allowed types: JPG, JPEG, PNG, GIF )</>
              ) : (
                profileData.email
              )}
            </p>
          </div>
        </div>
        <hr />
        <div className=" space-y-6">
          {/* <h3 className="text-lg font-semibold mb-4">Personal Details</h3> */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Business Name</Label>
              <Input
                name="business_name"
                value={profileData.business_name}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="mt-1"
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/^\s+/, '')
                }}
              />
            </div>
            <div>
              <Label>Business Type</Label>
              <div className="mt-1">
                <Input
                  value={
                    businessTypeOptions.find((t) => t.id === profileData.business_type)?.label || ''
                  }
                  disabled
                  className="bg-gray-100"
                />
              </div>
            </div>
            {/* <div>
              <Label>Business Type</Label>
              {isEditing ? (
                <Select value={profileData.business_type} onValueChange={handleBusinessTypeChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessTypeOptions.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={
                    businessTypeOptions.find((t) => t.id === profileData.business_type)?.label || ''
                  }
                  disabled
                  className="mt-1 bg-gray-100"
                />
              )}
            </div> */}
          </div>

          <div>
            <Label className="mb-1">Mobile Number</Label>
            {isEditing ? (
              <div className="space-y-1">
                <div className="flex gap-2">
                  <Select value={profileData.country_code} onValueChange={handleCountryCodeChange}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countryCodeOptions.map((country) => (
                        <SelectItem key={country.id} value={country.id}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    name="mobile"
                    value={profileData.mobile}
                    onChange={handleInputChange}
                    className="flex-1"
                    placeholder={`${getCurrentCountry().phoneLength} digits`}
                    maxLength={getCurrentCountry().phoneLength}
                  />
                </div>
                {phoneError && <p className="text-sm text-destructive">{phoneError}</p>}
              </div>
            ) : (
              <Input
                value={`${profileData.country_code} ${profileData.mobile}`}
                disabled
                className="mt-1 bg-gray-100"
              />
            )}
          </div>

          <div>
            <Label>Address</Label>
            <Input
              name="address"
              value={profileData.address}
              onChange={handleInputChange}
              disabled={!isEditing}
              className="mt-1"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/^\s+/, '')
              }}
            />
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Bank Details</h3>
            <div className="mb-4">
              <Label>Bank Name</Label>
              <Input
                name="bankName"
                value={profileData.bankName}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="mt-1"
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/^\s+/, '')
                }}
              />
              {bankError.bankName && (
                <p className="text-sm text-destructive">{bankError.bankName}</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Account Number</Label>
                <Input
                  type="number"
                  name="bankAccountNumber"
                  value={profileData.bankAccountNumber}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1"
                />
                {bankError.bankAccountNumber && (
                  <p className="text-sm text-destructive">{bankError.bankAccountNumber}</p>
                )}
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input
                  name="ifscCode"
                  value={profileData.ifscCode}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1"
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/^\s+/, '')
                  }}
                />
                {bankError.ifscCode && (
                  <p className="text-sm text-destructive">{bankError.ifscCode}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className=" w-full flex justify-end items-center mt-4 ">
        {isEditing ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditing(false)
                setSelectedImage(null)
                getProfile()
                setImageError('')
                setBankError('')
                setPhoneError('')
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-purple-600 cursor-pointer hover:text-purple-500 hover:bg-white shadow-2xl border hover:border-purple-500"
              onClick={handleSave}
              disabled={loading1}
            >
              {loading1 ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        ) : (
          <Button
            className="bg-purple-600 cursor-pointer hover:text-purple-500 hover:bg-white shadow-2xl border hover:border-purple-500"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </Button>
        )}
      </div>
    </div>
  )
}

export default BusinessProfileComponent
